/**
 * React Query hooks for the Well Servicing AI drafts (WS-6, fieldiq-api#1296).
 * Keys start ['wellServicing'] like every WS hook, so a WS write elsewhere
 * refreshes these too; reads spread LOAD_FAILURE_QUERY_OPTIONS (a 403 is an
 * answer, not a flake). A run, an approve and a dismiss each invalidate the WS
 * prefix — the register, the stored run, and (for a report draft approved into
 * its report) the report all move.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import { invalidateWs } from './useWellServicing';
import type { WsAiApproveBody, WsAiRunResult, WsAiStoredRun } from '../types/ws-ai';

/** `{ data }` → data, or null. Never the envelope. */
function unwrap<T>(body: unknown): T | null {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return null;
  const d = (body as { data?: unknown }).data;
  return d === undefined || d === null ? null : (d as T);
}

export const wsAiKeys = {
  digests: (agent?: string, jobId?: string, outcome?: string) => ['wellServicing', 'ai', 'digests', agent ?? '', jobId ?? '', outcome ?? ''] as const,
  digest: (id: string) => ['wellServicing', 'ai', 'digest', id] as const,
};

/** `outcome` is agent_runs.outcome, filtered by the API before its 20-row limit (fieldiq-api#1307). */
export function useWsAiDigests(filters: { agent?: string; jobId?: string; outcome?: string } = {}, enabled = true) {
  const { agent, jobId, outcome } = filters;
  return useQuery({
    queryKey: wsAiKeys.digests(agent, jobId, outcome),
    queryFn: async () => {
      const params: { agent?: string; jobId?: string; outcome?: string } = {};
      if (agent) params.agent = agent;
      if (jobId) params.jobId = jobId;
      if (outcome) params.outcome = outcome;
      const d = unwrap<WsAiStoredRun[]>((await api.wellServicing.ai.digests(Object.keys(params).length ? params : undefined)).data);
      return Array.isArray(d) ? d : [];
    },
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWsAiDigest(id: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: wsAiKeys.digest(id ?? ''),
    queryFn: async () => unwrap<WsAiStoredRun>((await api.wellServicing.ai.digest(id as string)).data),
    enabled: enabled && !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export type WsAiRunRequest =
  | { agent: 'ws_tour_summary'; tourId: string }
  | { agent: 'ws_ticket_preflight'; ticketId: string }
  | { agent: 'ws_ticket_preflight'; jobId: string }
  | { agent: 'ws_well_report_draft'; reportId: string }
  | { agent: 'ws_compliance_digest' };

export function useRunWsAi() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: WsAiRunRequest): Promise<WsAiRunResult | null> => {
      const ai = api.wellServicing.ai;
      let res;
      if (req.agent === 'ws_tour_summary') res = await ai.tourSummary(req.tourId);
      else if (req.agent === 'ws_ticket_preflight') res = 'ticketId' in req ? await ai.ticketPreflight(req.ticketId) : await ai.jobPreflight(req.jobId);
      else if (req.agent === 'ws_well_report_draft') res = await ai.wellReportDraft(req.reportId);
      else res = await ai.complianceDigest();
      return unwrap<WsAiRunResult>(res.data);
    },
    onSuccess: () => invalidateWs(qc),
  });
}

export function useReviewWsAi() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: { id: string; action: 'approve'; edit?: WsAiApproveBody } | { id: string; action: 'dismiss' }) => {
      const res = req.action === 'approve'
        ? await api.wellServicing.ai.approve(req.id, req.edit ?? {})
        : await api.wellServicing.ai.dismiss(req.id);
      return unwrap<WsAiStoredRun>(res.data);
    },
    onSettled: () => invalidateWs(qc),
  });
}
